import React, { useMemo, useState } from 'react';
import {
  Users,
  Eye,
  LayoutGrid,
  Tag,
  Database,
  BookOpen,
  RotateCcw,
  ArrowDown,
  Zap,
  Info,
  Check,
} from 'lucide-react';
import Header from '../components/Header';
import TierCard from '../components/TierCard';
import GradientSlider from '../components/GradientSlider';
import LiveEstimate from '../components/LiveEstimate';
import CompareTiers from '../components/CompareTiers';
import {
  TIERS,
  VIEWER_PRICE,
  APP_PRICE,
  TAG_PACK_PRICE,
  HISTORIAN_OPTIONS,
  DASHBOARD_OPTIONS,
} from '../mock/pricingData';
import { useCurrency } from '../context/CurrencyContext';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import { useLocation, useNavigate } from 'react-router-dom';
import PtiAssetsMode from './PtiAssetsMode';

const SectionCard = ({ children, className = '' }) => (
  <div
    className={`rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-7 ${className}`}
  >
    {children}
  </div>
);

const StatTile = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
    <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] font-semibold uppercase text-white/50">
      <Icon className="w-3.5 h-3.5 text-white/40" />
      {label}
    </div>
    <div className="mt-3 text-2xl font-semibold text-white tabular-nums">
      {typeof value === 'number' ? value.toLocaleString('en-US') : value}
    </div>
  </div>
);

const RadioRow = ({ selected, onSelect, id, name, price, perLabel, description, big }) => {
  const { formatMoney } = useCurrency();
  return (
  <button
    type="button"
    onClick={() => onSelect(id)}
    className={`w-full flex items-center gap-4 rounded-xl border px-4 py-4 text-left transition-colors ${
      selected
        ? 'border-emerald-400/60 bg-emerald-400/[0.05]'
        : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
    }`}
  >
    <span
      className={`h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${
        selected ? 'border-emerald-400 bg-emerald-400' : 'border-white/25'
      }`}
    >
      {selected && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
    </span>
    <div className="flex-1 min-w-0">
      <div className="text-white font-medium text-[14.5px]">{name}</div>
      {description && (
        <div className="mt-0.5 text-xs text-white/55">{description}</div>
      )}
    </div>
    {price > 0 && (
      <div className="text-right flex-shrink-0">
        <div className={`font-semibold ${big ? 'text-lg' : 'text-base'} text-white tabular-nums`}>
          {formatMoney(price)}
        </div>
        <div className="text-[11px] text-white/50 uppercase tracking-wider">{perLabel}</div>
      </div>
    )}
  </button>
  );
};

const OptionCard = ({ selected, onSelect, id, icon: Icon, name, description, price, perLabel }) => {
  const { formatMoney } = useCurrency();
  return (
  <button
    type="button"
    onClick={() => onSelect(id)}
    className={`text-left rounded-xl border p-4 transition-colors ${
      selected
        ? 'border-emerald-400/60 bg-emerald-400/[0.05]'
        : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
    }`}
  >
    <div className="flex items-start gap-3">
      <span className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-white/70" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-white font-semibold text-[14.5px]">{name}</div>
        <div className="mt-1 text-xs text-white/55 leading-relaxed">{description}</div>
        <div className="mt-3 flex items-baseline gap-1">
          <span className="text-white font-semibold tabular-nums">{formatMoney(price)}</span>
          <span className="text-[11px] text-white/50 uppercase tracking-wider">{perLabel}</span>
        </div>
      </div>
    </div>
  </button>
  );
};

const PricingEstimator = () => {
  const { formatMoney } = useCurrency();
  const { user, isAdmin } = useAuth();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const isStreamlineRoute = location.pathname.startsWith('/streamline');
  // On /streamline the brand is forced to "Streamline" regardless of the global setting.
  // On /pti the global brand decides the tier prefix (Itanta or Streamline).
  const activeTierPrefix = isStreamlineRoute ? 'Streamline' : brand.tierPrefix;
  const brandName = isStreamlineRoute ? 'Streamline360' : (brand.key === 'streamline' ? 'Streamline' : 'PTI');
  const eyebrow = isStreamlineRoute
    ? 'Streamline360 · Prompt-to-Insights'
    : (brand.key === 'streamline' ? 'Streamline · Prompt-to-Insights' : 'Prompt-to-Insights');
  const isStreamline = isStreamlineRoute; // keep var name used below for hero gradient
  const allowedModes = (!user || isAdmin)
    ? ['concurrent', 'assets']
    : (user.allowed_pti_modes && user.allowed_pti_modes.length ? user.allowed_pti_modes : ['concurrent', 'assets']);
  const canConcurrent = allowedModes.includes('concurrent');
  const canAssets = allowedModes.includes('assets');
  const [mode, setMode] = useState(canConcurrent ? 'concurrent' : 'assets');
  const [selectedTier, setSelectedTier] = useState('business');
  const [addlCreators, setAddlCreators] = useState(0);
  const [viewers, setViewers] = useState(10);
  const [extraApps, setExtraApps] = useState(0);
  const [extraTagPacks, setExtraTagPacks] = useState(0);
  const [historian, setHistorian] = useState('none');
  const [dashboard, setDashboard] = useState('none');
  const [billing, setBilling] = useState('monthly');

  const tier = TIERS.find((t) => t.id === selectedTier);
  const isEnterprise = tier.id === 'enterprise';
  // Re-brand tier names based on the active brand (route-forced on /streamline, global elsewhere)
  const brandedTiers = useMemo(
    () => TIERS.map((t) => ({ ...t, name: t.name.replace(/^Itanta\s/, `${activeTierPrefix} `) })),
    [activeTierPrefix],
  );
  const brandedTier = brandedTiers.find((t) => t.id === selectedTier);

  // On tier switch, clamp viewers to at least included seats
  React.useEffect(() => {
    if (typeof tier.includedViewers === 'number' && viewers < tier.includedViewers) {
      setViewers(tier.includedViewers);
    }
  }, [selectedTier]);

  const includedViewers = typeof tier.includedViewers === 'number' ? tier.includedViewers : 0;
  const extraViewers = Math.max(0, viewers - includedViewers);
  const creatorPricePerSeat =
    typeof tier.additionalCreatorPrice === 'number' ? tier.additionalCreatorPrice : 0;

  const mrr = useMemo(() => {
    if (isEnterprise) return 0;
    return (
      tier.price +
      addlCreators * creatorPricePerSeat +
      extraViewers * VIEWER_PRICE +
      extraApps * APP_PRICE +
      extraTagPacks * TAG_PACK_PRICE
    );
  }, [
    isEnterprise,
    tier.price,
    addlCreators,
    creatorPricePerSeat,
    extraViewers,
    extraApps,
    extraTagPacks,
  ]);

  const historianAnnual = HISTORIAN_OPTIONS.find((h) => h.id === historian).price;
  const dashboardOneTime = DASHBOARD_OPTIONS.find((d) => d.id === dashboard).price;

  const breakdown = [
    { label: `Base · ${brandedTier.name.replace(/^(Itanta|Streamline)\s/, '')}`, value: `${formatMoney(tier.price)}/mo` },
    {
      label: `+ ${addlCreators} Additional Creators`,
      value: `${formatMoney(addlCreators * creatorPricePerSeat)}/mo`,
    },
    {
      label: `${viewers} View-Only Seats · ${includedViewers} incl. · ${extraViewers} billable @ ${formatMoney(VIEWER_PRICE)}`,
      value: `${formatMoney(extraViewers * VIEWER_PRICE)}/mo`,
    },
    {
      label: `+ ${extraApps} Extra Applications @ ${formatMoney(APP_PRICE)}`,
      value: `${formatMoney(extraApps * APP_PRICE)}/mo`,
    },
    {
      label: `+ ${extraTagPacks} Tag Packs · ${extraTagPacks * 100} tags @ ${formatMoney(TAG_PACK_PRICE)}`,
      value: `${formatMoney(extraTagPacks * TAG_PACK_PRICE)}/mo`,
    },
  ];

  const resetAll = () => {
    setSelectedTier('business');
    setAddlCreators(0);
    setViewers(10);
    setExtraApps(0);
    setExtraTagPacks(0);
    setHistorian('none');
    setDashboard('none');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0d] text-white relative overflow-x-hidden">
      {/* subtle background gradient */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(1200px 500px at 15% -10%, rgba(34,211,238,0.10), transparent 60%), radial-gradient(1000px 600px at 90% 5%, rgba(168,85,247,0.10), transparent 60%), radial-gradient(800px 500px at 50% 40%, rgba(52,211,153,0.05), transparent 60%)',
        }}
      />
      {/* grain */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.03] mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence baseFrequency=%220.9%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")',
        }}
      />

      <Header />

      <main className="relative max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        {/* Hero */}
        <section className="pt-14 md:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-white/70">
              {eyebrow}
            </span>
            <span className="text-white/25">·</span>
            <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-white/70">
              Pricing Estimator
            </span>
          </div>
          <h1 className="mt-6 text-4xl md:text-6xl font-semibold tracking-tight leading-[1.05] max-w-4xl">
            Size your {brandName} deployment{' '}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: isStreamline
                  ? 'linear-gradient(90deg, #34d399 0%, #22d3ee 100%)'
                  : 'linear-gradient(90deg, #22d3ee 0%, #a855f7 100%)',
              }}
            >
              in real time.
            </span>
          </h1>
          <p className="mt-5 max-w-2xl text-white/60 leading-relaxed text-[15px]">
            {mode === 'concurrent'
              ? 'A hybrid concurrent-licensing model built for manufacturing floors. Pick a tier, dial in creator and view-only seats, and see MRR & ARR calculated instantly.'
              : 'Pure-SaaS licensing by assets monitored + users. Real-time protocols only (OPC UA, MQTT, SQL DB). Includes mandatory onboarding consulting.'}
          </p>

          {/* Mode switcher */}
          {(canConcurrent && canAssets) ? (
          <div className="mt-8 inline-flex p-1 rounded-full bg-white/[0.04] border border-white/10">
            <button
              onClick={() => setMode('concurrent')}
              className={`h-10 px-5 rounded-full text-[12px] font-semibold tracking-wider uppercase transition-colors ${
                mode === 'concurrent' ? 'bg-white text-black' : 'text-white/60 hover:text-white/85'
              }`}
            >
              Hybrid concurrent
            </button>
            <button
              onClick={() => setMode('assets')}
              className={`h-10 px-5 rounded-full text-[12px] font-semibold tracking-wider uppercase transition-colors flex items-center gap-2 ${
                mode === 'assets' ? 'bg-white text-black' : 'text-white/60 hover:text-white/85'
              }`}
            >
              Assets & users
              <span className={`text-[9.5px] px-1.5 py-0.5 rounded-full font-semibold ${mode === 'assets' ? 'bg-cyan-400/20 text-cyan-700' : 'bg-cyan-400/15 text-cyan-300'}`}>
                New · SaaS
              </span>
            </button>
          </div>
          ) : (
            <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-300" />
              <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-white/70">
                {mode === 'concurrent' ? 'Hybrid concurrent licensing' : 'Assets & users licensing'}
              </span>
            </div>
          )}
        </section>

        {mode === 'assets' ? (
          <section className="mt-10">
            <PtiAssetsMode />
          </section>
        ) : (
          <>
        {/* Main grid */}
        <section id="estimator" className="mt-12 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6 lg:gap-8">
          {/* Left column */}
          <div className="space-y-6">
            {/* Choose tier */}
            <SectionCard>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-semibold text-white">Choose a tier</h2>
                <button
                  onClick={resetAll}
                  className="flex items-center gap-1.5 text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60 hover:text-white transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset all
                </button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {brandedTiers.map((t) => (
                  <TierCard
                    key={t.id}
                    tier={t}
                    selected={selectedTier === t.id}
                    onSelect={setSelectedTier}
                  />
                ))}
              </div>

              {/* Customize hint */}
              <div className="mt-5 flex items-start gap-3 rounded-xl border border-emerald-400/20 bg-emerald-400/[0.04] px-4 py-3">
                <ArrowDown className="w-4 h-4 text-emerald-300 mt-0.5 flex-shrink-0" />
                <p className="text-[13px] text-white/75">
                  <span className="text-white font-semibold">Customize your plan below</span>
                  <span className="text-white/50"> · dial in creators, view-only seats, apps &amp; data tags to build your exact quote.</span>
                </p>
              </div>

              {/* Stat tiles */}
              <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatTile icon={Users} label="Included creator seats" value={tier.includedCreators} />
                <StatTile icon={Eye} label="Included view-only seats" value={tier.includedViewers} />
                <StatTile icon={LayoutGrid} label="Max applications" value={tier.maxApps} />
                <StatTile icon={Tag} label="Max data tags monitored" value={tier.maxTags} />
              </div>

              <div className="mt-5 flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
                <Zap className="w-4 h-4 text-cyan-300" />
                <div className="text-[13px]">
                  <span className="text-white font-semibold">All connectors included</span>
                  <span className="text-white/50"> · MQTT, OPC UA, SQL, Excel, CSV.</span>
                </div>
              </div>
            </SectionCard>

            {/* Additional creators */}
            <SectionCard>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Additional concurrent creators</h3>
                <div className="text-[11px] tracking-[0.15em] font-semibold uppercase text-white/50">
                  {isEnterprise ? 'Custom' : formatMoney(creatorPricePerSeat)} / seat / month
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <div className="text-[13px] text-white/60">Beyond the included seats</div>
                <div className="text-3xl font-semibold text-white tabular-nums">{addlCreators}</div>
              </div>
              <div className="mt-4">
                <GradientSlider
                  value={addlCreators}
                  min={0}
                  max={20}
                  onChange={setAddlCreators}
                  marks={['0', '10', '20']}
                />
              </div>
            </SectionCard>

            {/* View-only seats */}
            <SectionCard>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">View-only plant-floor seats</h3>
                <div className="text-[11px] tracking-[0.15em] font-semibold uppercase text-white/50">
                  {includedViewers} included · {formatMoney(VIEWER_PRICE)} / additional user / month
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <div className="text-[13px] text-white/60">
                  On-premise dashboards, no prompt/AI rights
                </div>
                <div className="text-3xl font-semibold text-white tabular-nums">{viewers}</div>
              </div>
              <div className="mt-2 text-[12px] text-white/45">
                {includedViewers} of {includedViewers} included
                <span className="mx-1.5">·</span>
                <span className="text-emerald-300">{extraViewers} billable</span>
              </div>
              <div className="mt-4">
                <GradientSlider
                  value={viewers}
                  min={0}
                  max={200}
                  onChange={setViewers}
                  marks={['0', '100', '200']}
                />
              </div>
              <div className="mt-4 flex items-start gap-3 rounded-lg bg-white/[0.02] border border-white/10 px-4 py-3">
                <Info className="w-4 h-4 text-cyan-300 mt-0.5 flex-shrink-0" />
                <p className="text-[12.5px] text-white/60 leading-relaxed">
                  Viewers have zero prompt/AI rights. They interact with pre-built dashboards
                  executing entirely on-premise, generating{' '}
                  <span className="text-emerald-300 font-semibold">{formatMoney(0)}</span> cloud LLM token overhead.
                </p>
              </div>
            </SectionCard>

            {/* Extra apps */}
            <SectionCard>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Add-on: extra applications</h3>
                <div className="text-[11px] tracking-[0.15em] font-semibold uppercase text-white/50">
                  {formatMoney(APP_PRICE)} / application / month
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <div className="text-[13px] text-white/60">
                  Beyond the {typeof tier.maxApps === 'number' ? tier.maxApps : ''} tier applications
                </div>
                <div className="text-3xl font-semibold text-white tabular-nums">{extraApps}</div>
              </div>
              <div className="mt-4">
                <GradientSlider
                  value={extraApps}
                  min={0}
                  max={20}
                  onChange={setExtraApps}
                  marks={['0', '10', '20']}
                />
              </div>
            </SectionCard>

            {/* Extra tags */}
            <SectionCard>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Add-on: extra data tags</h3>
                <div className="text-[11px] tracking-[0.15em] font-semibold uppercase text-white/50">
                  {formatMoney(TAG_PACK_PRICE)} / 100-tag pack / month
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <div className="text-[13px] text-white/60">
                  Beyond the {typeof tier.maxTags === 'number' ? tier.maxTags.toLocaleString('en-US') : ''} tier tags · packs of 100
                </div>
                <div className="text-3xl font-semibold text-white tabular-nums">{extraTagPacks}</div>
              </div>
              <div className="mt-4">
                <GradientSlider
                  value={extraTagPacks}
                  min={0}
                  max={500}
                  onChange={setExtraTagPacks}
                  marks={['0', '250 packs · 25k tags', '500 packs · 50k tags']}
                />
              </div>
            </SectionCard>

            {/* Historian */}
            <SectionCard>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Historian connectors</h3>
                <div className="text-[11px] tracking-[0.15em] font-semibold uppercase text-white/50">
                  Optional · annual subscription
                </div>
              </div>
              <div className="mt-5 flex items-start gap-3 rounded-lg bg-white/[0.02] border border-white/10 px-4 py-3">
                <Database className="w-4 h-4 text-purple-300 mt-0.5 flex-shrink-0" />
                <p className="text-[12.5px] text-white/65 leading-relaxed">
                  <span className="text-white/80">Built-in on every tier:</span>{' '}
                  <span className="text-cyan-300">MQTT</span> ·{' '}
                  <span className="text-cyan-300">OPC UA</span> ·{' '}
                  <span className="text-cyan-300">SQL</span> ·{' '}
                  <span className="text-cyan-300">Excel</span> ·{' '}
                  <span className="text-cyan-300">CSV</span>.{' '}
                  Business allows up to 5 datasources (files / tables / endpoints) per connector.
                  Historian connectors below are annual subscriptions.
                </p>
              </div>
              <div className="mt-4 space-y-3">
                <RadioRow
                  {...HISTORIAN_OPTIONS[0]}
                  selected={historian === 'none'}
                  onSelect={setHistorian}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {HISTORIAN_OPTIONS.slice(1).map((h) => (
                    <OptionCard
                      key={h.id}
                      {...h}
                      icon={Database}
                      selected={historian === h.id}
                      onSelect={setHistorian}
                    />
                  ))}
                </div>
              </div>
            </SectionCard>

            {/* Dashboards */}
            <SectionCard>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Dashboard templates</h3>
                <div className="text-[11px] tracking-[0.15em] font-semibold uppercase text-white/50">
                  Optional · one-time · not part of MRR
                </div>
              </div>
              <div className="mt-5 flex items-start gap-3 rounded-lg bg-white/[0.02] border border-white/10 px-4 py-3">
                <BookOpen className="w-4 h-4 text-purple-300 mt-0.5 flex-shrink-0" />
                <p className="text-[12.5px] text-white/65 leading-relaxed">
                  <span className="text-white/80">Domain-knowledge dashboards packaged by Streamline360.</span>{' '}
                  Pick a pack to accelerate go-live, or skip to build your own.
                </p>
              </div>
              <div className="mt-4 space-y-3">
                <RadioRow
                  {...DASHBOARD_OPTIONS[0]}
                  selected={dashboard === 'none'}
                  onSelect={setDashboard}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {DASHBOARD_OPTIONS.slice(1).map((d) => (
                    <OptionCard
                      key={d.id}
                      {...d}
                      icon={BookOpen}
                      selected={dashboard === d.id}
                      onSelect={setDashboard}
                    />
                  ))}
                </div>
              </div>
            </SectionCard>
          </div>

          {/* Right column: live estimate */}
          <div>
            <LiveEstimate
              tier={brandedTier}
              billing={billing}
              setBilling={setBilling}
              addlCreators={addlCreators}
              totalViewers={viewers}
              extraViewers={extraViewers}
              extraApps={extraApps}
              extraTagPacks={extraTagPacks}
              mrr={mrr}
              breakdown={breakdown}
              historianAnnual={historianAnnual}
              dashboardOneTime={dashboardOneTime}
            />
          </div>
        </section>

        {/* Compare tiers */}
        <CompareTiers selected={selectedTier} />
          </>
        )}

        {/* Footer */}
        <footer className="mt-24 pt-8 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-[12px] text-white/40">
            © {new Date().getFullYear()} {isStreamlineRoute ? 'Streamline360' : brand.companyLegalName} · Prompt-to-Insights pricing estimator
          </div>
          <div className="flex items-center gap-6 text-[12px] text-white/40">
            <a href="#" className="hover:text-white/70 transition-colors">Terms</a>
            <a href="#" className="hover:text-white/70 transition-colors">Privacy</a>
            <a href="#" className="hover:text-white/70 transition-colors">Contact</a>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default PricingEstimator;
